import { useSelector } from "react-redux";
import { selectCurrentOrganization } from "../../app/slices/UserSlice";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import { useEffect, useRef, useState } from "react";
import {
  addPagedSituations,
  initializeSituations,
  selectSituations,
} from "../../app/slices/SituationSlice";
import {
  selectFilteredCategory,
  selectPropFilter,
} from "../../app/slices/PropLibrarySlice";
import { useDispatch } from "react-redux";
import { PropService } from "../../services/PropService";
import { SituationService } from "../../services/SituationService";

export default function useSituationPagination() {
  // needed for service
  const orgId = useSelector(selectCurrentOrganization)?.id;
  const token = useSelector(selectToken);

  // pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;

  // situations
  const allSituations = useSelector(selectSituations);

  // filters
  const filteredCategory = useSelector(selectFilteredCategory); // category - filter
  const propFilter = useSelector(selectPropFilter); // text - filter

  // slice
  const dispatch = useDispatch();

  /**
   * Bumped on every reset (a new category or search query). Requests carry the
   * value they were issued under and drop their result if it no longer matches
   * — otherwise a slow response for an older query lands after a newer one and
   * overwrites the list the user is looking at.
   */
  const requestRef = useRef(0);

  useEffect(() => {
    LoadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCategory, propFilter]);

  async function LoadFirstPage() {
    if (!orgId) return;

    setCurrentPage(1);
    const requestId = ++requestRef.current;

    const result = await SituationService.GetSituationsFromPage(
      orgId,
      token,
      1,
      pageSize,
      filteredCategory,
      propFilter
    );
    if (requestId !== requestRef.current) return;

    dispatch(
      initializeSituations({
        totalCount: result.totalCount,
        firstPage: result.props,
      })
    );
  }

  // 2. onRerender
  const onRerender = async (
    amountOfColumns: number,
    rowStartIndex: number,
    rowStopIndex: number
  ) => {
    if (!orgId || !token) return;

    // Page fills are not resets, so this reads the current id rather than
    // bumping it: it only tells us whether the query changed under us.
    const requestId = requestRef.current;

    var startIndex = amountOfColumns * rowStartIndex;
    var endIndex = amountOfColumns * rowStopIndex + (amountOfColumns - 1);
    var startPage = Math.floor(startIndex / 10);
    var endPage = Math.ceil(endIndex / 10);

    // load next page
    for (var s = startPage; s < endPage; s++) {
      // The virtualiser reports the range it rendered last, which a narrowing
      // search can leave pointing past the end of the new, shorter array.
      if (allSituations[10 * s]?.id !== "unloaded") continue;

      const result = await SituationService.GetSituationsFromPage(
        orgId,
        token,
        s + 1,
        pageSize,
        filteredCategory,
        propFilter
      );

      if (!result) return;
      if (requestId !== requestRef.current) return;

      dispatch(addPagedSituations({ pageNumber: s, assets: result.props }));
    }
  };

  return { LoadFirstPage, onRerender } as const;
}
